<script setup lang="ts">
import t from "~/utils/translate";
import { mdiLockOutline } from "@mdi/js";
import { IJackpotEvent } from "./Selections.vue";
import Event from "./Event.vue";
import { IOutcome } from "~/types/sports/outcome";
import ResultsIcon from "~/components/layout/TrackRacing/Shared/ResultsIcon.vue";
import PickToPlaceModal from "./PickToPlaceModal.vue";
import SubmissionConfirmation from "./SubmissionConfirmation.vue";
import { IPrice } from "~/types/sports/price";
import { IResult } from "~/types/racing/race-results";
import api from "~/utils/auth/api";
import { useQuery } from "@tanstack/vue-query";
import { IJackpotBet, IJackpotBetSlip, IJackPotMarket, IStrikeResponse } from "~/types/racing/jackpot-bets";
import { useAuth } from "~/store/auth";
import { isMobile } from "~/utils/constants";
import { DateTime } from "luxon";

export interface IOutcomeWithPrices extends IOutcome {
	price: IPrice;
}
export interface IJackpotEvents extends IJackpotEvent {
	raceWinnerMarket: IJackPotMarket;
	placeMarket: IJackPotMarket;
	raceWinnerOutcomes: IOutcomeWithPrices[];
	placeOutcomes: IOutcomeWithPrices[];
}

interface EventsProps {
	events: IJackpotEvent[] | undefined;
	raceResults: IResult[] | undefined;
	jackpotName: string | undefined;
	isJackpotOpen: boolean | false;
}

const auth = useAuth();
const emit = defineEmits(["jackpot-completed"]);
const selectedLines = ref<IOutcome[]>([]);
const jackpotBets = ref<IJackpotBet[]>([]);
const isPlaceModalActive = ref<boolean>(false);
const isSubmitConfirmationActive = ref<boolean>(false);
const submissionResponse = ref<{ response: IStrikeResponse; betsOutcomes: IOutcome[] }>();

const props = defineProps<EventsProps>();

const findRaceWinnerMarket = (markets: IJackPotMarket[]) => {
	return markets.find((m) => m.market.name.includes("Race Winner"));
};

const marketOutcomesWithPrices = (
	outcomesWithPrices: { outcomes: IOutcome[]; prices: IPrice[] },
	raceWinnerMarket: IJackPotMarket,
	isRaceWinner: boolean
) => {
	const marketOutcomes = isRaceWinner
		? outcomesWithPrices.outcomes.filter((outcome) => Number(outcome.marketId) === raceWinnerMarket?.market.marketId.marketId)
		: outcomesWithPrices.outcomes.filter((outcome) => outcome.marketId !== raceWinnerMarket?.market.marketId);
	return marketOutcomes.map((outcome) => ({ ...outcome, price: outcomesWithPrices.prices.find((price) => price.outcomeId === outcome.outcomeId) }));
};

const eventsWithMarketsOutcomes = computed(() => {
	if (!props.events) return [];

	return props.events
		.map((event) => {
			const raceWinnerMarket = findRaceWinnerMarket(event.markets) as IJackPotMarket;

			return {
				...event,
				raceWinnerMarket,
				placeMarket: event.markets.find((e) =>
					event.outcomesAndPrices.outcomes.some(
						(o) => o.marketId === e.market.marketId && e.market.marketId.marketId !== raceWinnerMarket.market.marketId.marketId
					)
				),
				raceWinnerOutcomes: marketOutcomesWithPrices(event.outcomesAndPrices, raceWinnerMarket, true),
				placeOutcomes: marketOutcomesWithPrices(event.outcomesAndPrices, raceWinnerMarket, false),
			};
		})
		.sort((a, b) => a.raceNumber - b.raceNumber) as IJackpotEvents[];
});

const randomPicks = (outcomes: IOutcome[]) => {
	const randomIndex = Math.floor(Math.random() * outcomes.length);
	const bet = outcomes[randomIndex];
	return bet;
};

const pickFavorite = () => {
	selectedLines.value = [];
	const selection = eventsWithMarketsOutcomes.value.map((e) => e.raceWinnerOutcomes[0]);
	selectedLines.value.push(...selection);
};

const handleOutcomeSelection = (outcome: IOutcome) => {
	if (!props.isJackpotOpen) return;
	if ((outcome && !outcome.isTradingActive) || jackpotBets.value.length > 0) return;
	if (outcome) {
		const doesSelectionExist = selectedLines.value.some((selected) => selected.eventId === outcome.eventId);
		if (!doesSelectionExist) {
			selectedLines.value.push(outcome);
		} else {
			const selectionIndex = selectedLines.value.findIndex((selectedIndex) => selectedIndex.eventId === outcome.eventId);
			selectedLines.value.splice(selectionIndex, 1);
			selectedLines.value.push(outcome);
		}
	} else {
		selectedLines.value = [];
		eventsWithMarketsOutcomes.value?.forEach((event) => {
			const eventOutcomes = event.raceWinnerOutcomes.filter((o) => o.isTradingActive);
			selectedLines.value.push(randomPicks(eventOutcomes));
		});
	}
};

const { isLoading } = useQuery<{ betslips: IJackpotBetSlip[] }>({
	queryKey: ["jackpot-selection"],
	refetchOnWindowFocus: false,
	queryFn: () =>
		api({
			url: "/Betting/GetSynapseJackpotBetslips",
			type: "universal",
			apiVersion: "v1",
			options: { method: "POST" },
		}),
	onSuccess: (data) => {
		const relatedEventsIds = eventsWithMarketsOutcomes.value.map((e) => e.event.eventId);
		const bets = data.betslips.find((b) => b.league === props.jackpotName && b.jackpotBets.some((e) => relatedEventsIds.includes(e.eventId)));
		jackpotBets.value = bets?.jackpotBets || [];
		if (jackpotBets.value.length > 0) {
			emit("jackpot-completed", jackpotBets.value);
		}
	},
});

const handleConfirmation = ($event: { response: IStrikeResponse; betsOutcomes: IOutcome[] }) => {
	isPlaceModalActive.value = false;
	isSubmitConfirmationActive.value = true;
	submissionResponse.value = $event;
};

const jackpotClosed = computed(
	() =>
		DateTime.now().setZone("Africa/Johannesburg") >
		DateTime.fromSeconds(eventsWithMarketsOutcomes.value[0].event.expectedStartEpoch).setZone("Africa/Johannesburg").minus({ minutes: 10 })
);

const strikeJackPot = () => {
	if (!auth.loggedIn) {
		auth.modals.login = true;
		return;
	}
	isPlaceModalActive.value = true;
};

defineExpose({
	handleOutcomeSelection,
	pickFavorite,
});
</script>

<template>
	<div>
		<div class="grid grid-cols-1 md:grid-cols-4 gap-2 leading-tight">
			<div v-for="event in eventsWithMarketsOutcomes" :key="event.event.eventId" class="bg-light-50 dark:bg-dark-800 rounded-md text-xs">
				<div class="flex items-center justify-between bg-light-400 dark:bg-dark-700 text-dark-300 dark:text-light-50 p-2 rounded-t-md font-bold">
					<div class="flex items-center">
						<ResultsIcon v-if="!event.event.isActive" />
						<div class="mt-[2px]">
							<UiDateTime class="ml-1 text-dark-700 dark:text-light-50" :date="event.event.venueEpoch" time dateFormat=" " timeFormat=" HH:mm" />
						</div>
					</div>
					<UiIcon v-if="!event.event.isActive" :path="mdiLockOutline" class="fill-dark-800 dark:fill-light-50 w-4 h-4 mr-1" />
				</div>
				<Event
					:event="event.event"
					:outcomes="event.raceWinnerOutcomes"
					:prices="event.outcomesAndPrices.prices"
					:raceResults="raceResults"
					:selectedLines="selectedLines"
					:jackpotBets="jackpotBets"
					@outcome-selected="handleOutcomeSelection($event)"
				/>
			</div>
		</div>

		<PickToPlaceModal
			v-if="isPlaceModalActive"
			:active="isPlaceModalActive"
			:selectedLines="selectedLines"
			:jackpotEvents="eventsWithMarketsOutcomes"
			:jackpotClosed="jackpotClosed"
			@set-active="(val) => (isPlaceModalActive = val)"
			@submit-confirmation="handleConfirmation($event)"
		/>

		<SubmissionConfirmation
			v-if="isSubmitConfirmationActive && submissionResponse"
			:active="isSubmitConfirmationActive"
			:betsAndResponse="submissionResponse"
			:events="events || []"
			@set-active="(val) => (isSubmitConfirmationActive = val)"
			@handle-close="isSubmitConfirmationActive = false"
		/>

		<div
			v-if="jackpotBets.length === 0"
			class="mx-auto rounded-t-md text-center bg-light-50 dark:bg-dark-800 mt-[40px]"
			:class="isMobile ? 'mb-[50px] w-full fixed bottom-0 z-10 right-0' : 'w-[400px]'"
		>
			<div class="flex justify-between p-2 items-center">
				<div class="text-sm">{{ t("choose-one-horse-per-race-and-confirm-your-selection") }}</div>
				<div class="font-bold text-sm">
					<span v-if="jackpotBets.length > 0"> 8/8 </span>
					<span v-else> {{ selectedLines.length }}/8 </span>
				</div>
			</div>

			<UiButton @click="strikeJackPot()" :label="t('continue')" size="medium" class="mb-3" :disabled="selectedLines.length < 8" />
		</div>
	</div>
</template>
